import { useEffect, useRef, useState } from 'react'
import SplitLegend from './SplitLegend'
import { getCCAA } from '../informes/syb7Sources'

// Mapa interactivo Leaflet con las capas WMS oficiales de riesgos naturales
// para el informe SyB 7:
//   · Sismicidad NCSE-02 y PGA 475a (IGN geofísica) — siempre nacionales
//   · Peligro y zonas de riesgo grave de incendios (WMS solo Generalitat, se
//     activa solo cuando la CCAA del proyecto es Cataluña)
//   · Inundaciones T=500 (MITECO, contexto) — nacional
//
// Requiere que Leaflet esté cargado en window.L (via <script> en index.html).

const WMS_IGN         = 'https://www.ign.es/wms-inspire/geofisica'
const WMS_PCIVIL      = 'https://pcivil.icgc.cat/ogc/geoservei?map=/opt/idec/dades/pcivil/risc_incendis.map'
const WMS_MITECO      = 'https://servicios.idee.es/wms-inspire/riesgos-naturales/inundaciones'
const WMS_IDECAN_ZARI = 'https://idecan2.grafcan.es/ServicioWMS/ZARI'                         // Incendios Canarias
const WMS_IGN_VULCAN  = 'https://wms-volcanologia.ign.es/volcanologia'                        // Vulcanología Canarias
const WMS_IGME_MOVES  = 'https://mapas.igme.es/gis/services/BasesDatos/IGME_BDMoves_ES/MapServer/WMSServer'  // Movimientos del terreno nacional
const WMS_ICGC_RISC   = 'https://geoserveis.icgc.cat/servei/catalunya/riscos-geologics/wms/service'          // Riesgos geológicos ICGC

// GetLegendGraphic — la Generalitat exige version=1.3.0 explícito
function legendUrl(wmsBase, layerName, extraParams = '') {
  const p = new URLSearchParams({
    service: 'WMS',
    request: 'GetLegendGraphic',
    format: 'image/png',
    layer: layerName,
    sld_version: '1.1.0',
  })
  return `${wmsBase}${wmsBase.includes('?') ? '&' : '?'}${extraParams}${extraParams ? '&' : ''}${p.toString()}`
}

// mode = 'wide'  → mostrar leyenda a tamaño natural (hasta el ancho del cuadro)
// mode = 'split' → dividir en N tiras horizontales dentro del cuadro (para verticales altas)
// mode = 'thumb' → miniatura pequeña con link (para verticales gigantes)
// mode = 'clip'  → recortar solo el primer degradado de una imagen con múltiples barras (MITECO)
const LAYER_DOCS = [
  { key: 'ign_ncse02',    label: 'Sismicidad NCSE-02 (IGN)',            note: 'Aceleración sísmica básica a_b (g) · Anejo 1 NCSE-02',                mode: 'wide'  },
  { key: 'ign_pga475',    label: 'PGA 475a (IGN 2015)',                 note: 'Aceleración pico terreno · retorno 475 años (10 % en 50 a)',         mode: 'split', splits: 3 },
  { key: 'pcivil_perill', label: 'Perill incendi (Generalitat)',        note: 'Baix · Moderat · Alt · Molt alt · Extrem · escala municipal',        mode: 'wide'  },
  { key: 'pcivil_greu',   label: 'Zones risc greu incendi',             note: 'Alto riesgo forestal · Decret 64/1995 · Pla ALFA',                   mode: 'wide'  },
  { key: 'idecan_zari',   label: 'ZARI Canarias (Gobierno Canarias)',   note: 'Zonas de Alto Riesgo de Incendio Forestal · Decreto 100/2002 · IDECanarias', mode: 'wide' },
  { key: 'ign_erupciones', label: 'Erupciones históricas (IGN)',        note: 'Registro histórico de erupciones volcánicas · Red de Vigilancia Volcánica del IGN. Canarias.', mode: 'wide' },
  { key: 'igme_bdmoves',  label: 'Movimientos del terreno (IGME BDMoves)', note: 'Base nacional de deslizamientos, subsidencias, colapsos, expansividad. Cobertura toda España (incluye Canarias).', mode: 'wide' },
  { key: 'icgc_riscs',    label: 'Riscos geològics (ICGC · Catalunya)', note: 'Mapa de prevención 1:25 000 · inventario de fenómenos, deslizamientos y flujos torrenciales. Solo Cataluña.', mode: 'wide' },
  { key: 'miteco_t500',   label: 'Inundacions T=500 (MITECO, contexto)', note: 'Prob. baja · retorno 500 años · Directiva 2007/60/CE. Calado en metros — degradado azul claro → azul oscuro. Umbral peligrosidad grave RDPH art. 9.2: calado > 1 m.', mode: 'clip' },
]


export default function NaturalRisksMap({ latitude, longitude, addressLabel, province, height = 360 }) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const projectCCAA = getCCAA(province)
  const isCatalunya = projectCCAA === 'CATALUÑA'
  const isCanarias  = projectCCAA === 'CANARIAS'
  // Capa por defecto activa: cada CCAA arranca con su capa más relevante
  const defaultLayer = isCatalunya ? 'pcivil_perill'
    : isCanarias ? 'idecan_zari'
    : 'ign_ncse02'
  const [activeLayers, setActiveLayers] = useState([defaultLayer])

  useEffect(() => {
    if (!containerRef.current || latitude == null || longitude == null) return
    if (mapRef.current) return

    const initMap = () => {
      const L = window.L
      if (!L) { setTimeout(initMap, 200); return }
      const lat = Number(latitude); const lon = Number(longitude)
      if (!isFinite(lat) || !isFinite(lon)) return

      const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: false })
      mapRef.current = map
      map.setView([lat, lon], 14)

      // Esri World Light Gray Canvas (free, uso comercial permitido con atribución,
      // sin API key). OSM directo prohíbe uso comercial.
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 16, attribution: 'Tiles © Esri',
      }).addTo(map)

      // Capas base (siempre nacionales — cubren toda España)
      const layerDefs = [
        { key: 'ign_ncse02',    name: 'Sismicidad NCSE-02 (IGN)',           wms: WMS_IGN,    layer: 'HazardArea2002.NCSE-02', opacity: 0.4 },
        { key: 'ign_pga475',    name: 'PGA 475a (IGN 2015)',                wms: WMS_IGN,    layer: 'HazardArea2015.PGA475_p', opacity: 0.4 },
        { key: 'igme_bdmoves',  name: 'Movimientos del terreno (IGME)',     wms: WMS_IGME_MOVES, layer: '0',                  opacity: 0.7 },
        { key: 'miteco_t500',   name: 'Inundacions T=500 (MITECO)',         wms: WMS_MITECO, layer: 'NZ.Flood.FluvialT500',    opacity: 0.35 },
      ]
      // Capas específicas de Cataluña — solo se cargan si el proyecto está en esa CCAA
      if (isCatalunya) {
        layerDefs.push(
          { key: 'pcivil_perill', name: 'Perill incendi (Generalitat)',       wms: WMS_PCIVIL, layer: 'infocat_perill',          opacity: 0.55, version: '1.3.0' },
          { key: 'pcivil_greu',   name: 'Zones risc greu incendi',            wms: WMS_PCIVIL, layer: 'zones_risc_greu_incendis', opacity: 0.6,  version: '1.3.0' },
          { key: 'icgc_riscs',    name: 'Riscos geològics (ICGC)',            wms: WMS_ICGC_RISC, layer: 'G6FIA_PN',              opacity: 0.6,  version: '1.3.0' },
        )
      }
      // Capas específicas de Canarias (incendios ZARI + erupciones volcánicas)
      if (isCanarias) {
        layerDefs.push(
          { key: 'idecan_zari',    name: 'ZARI Canarias (Gobierno Canarias)',  wms: WMS_IDECAN_ZARI, layer: 'ZARI',        opacity: 0.6 },
          { key: 'ign_erupciones', name: 'Erupciones históricas (IGN)',        wms: WMS_IGN_VULCAN,  layer: 'erupciones',  opacity: 0.7 },
        )
      }
      const layers = {}
      const keyByName = {}
      for (const d of layerDefs) {
        const opts = { format: 'image/png', transparent: true, layers: d.layer, opacity: d.opacity, version: d.version || '1.3.0', attribution: d.name }
        const layer = L.tileLayer.wms(d.wms, opts)
        layers[d.name] = layer
        keyByName[d.name] = d.key
      }
      // Capa activa inicial según CCAA
      const initialLayerName = isCatalunya ? 'Perill incendi (Generalitat)'
        : isCanarias ? 'ZARI Canarias (Gobierno Canarias)'
        : 'Sismicidad NCSE-02 (IGN)'
      if (layers[initialLayerName]) layers[initialLayerName].addTo(map)

      const updateActive = () => {
        const active = []
        for (const d of layerDefs) {
          if (map.hasLayer(layers[d.name])) active.push(d.key)
        }
        setActiveLayers(active)
      }
      map.on('overlayadd overlayremove', updateActive)

      L.control.layers({ 'OpenStreetMap': map._layers[Object.keys(map._layers)[0]] }, layers, { collapsed: false, position: 'topright' }).addTo(map)

      const marker = L.marker([lat, lon]).addTo(map)
      marker.bindPopup(`<div style="font-family:'DM Sans',sans-serif;font-size:13px"><strong>Ubicación del activo</strong><br>${addressLabel || (lat.toFixed(5) + ', ' + lon.toFixed(5))}</div>`)
      marker.openPopup()
      L.control.scale({ imperial: false, metric: true }).addTo(map)
    }
    initMap()

    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null }
    }
  }, [latitude, longitude, addressLabel, isCatalunya, isCanarias])

  if (latitude == null || longitude == null) {
    return (
      <div style={{ padding: '0.75rem', fontSize: '0.78rem', color: '#7A6A5A', background: '#FAF7F3', border: '1px dashed #DDD5C8', borderRadius: 4 }}>
        Sin coordenadas guardadas para el edificio — no se puede renderizar el mapa.
      </div>
    )
  }

  // URL de leyenda por capa activa
  //   local  → PNG cacheado en /wms-legends/ (carga instantánea, siempre disponible)
  //   remote → URL viva del WMS oficial (para el link 'click para ampliar')
  const legendUrlFor = (key) => {
    if (key === 'ign_ncse02')     return '/wms-legends/ign_ncse02.png'
    if (key === 'ign_pga475')     return '/wms-legends/ign_pga475.png'
    if (key === 'pcivil_perill')  return '/wms-legends/pcivil_perill.png'
    if (key === 'pcivil_greu')    return '/wms-legends/pcivil_greu.png'
    if (key === 'miteco_t500')    return '/wms-legends/miteco_t500.png'
    // Capas nuevas — servimos leyenda remota del WMS directamente (no cacheada)
    if (key === 'idecan_zari')    return legendUrl(WMS_IDECAN_ZARI, 'ZARI',       'version=1.3.0')
    if (key === 'ign_erupciones') return legendUrl(WMS_IGN_VULCAN,  'erupciones', 'version=1.3.0')
    if (key === 'igme_bdmoves')   return legendUrl(WMS_IGME_MOVES,  '0',          'version=1.3.0')
    if (key === 'icgc_riscs')     return legendUrl(WMS_ICGC_RISC,   'G6FIA_PN',   'version=1.3.0')
    return null
  }
  const legendRemoteFor = (key) => {
    if (key === 'ign_ncse02')     return legendUrl(WMS_IGN,         'HazardArea2002.NCSE-02')
    if (key === 'ign_pga475')     return legendUrl(WMS_IGN,         'HazardArea2015.PGA475_p')
    if (key === 'pcivil_perill')  return legendUrl(WMS_PCIVIL,      'infocat_perill',            'version=1.3.0')
    if (key === 'pcivil_greu')    return legendUrl(WMS_PCIVIL,      'zones_risc_greu_incendis',  'version=1.3.0')
    if (key === 'miteco_t500')    return legendUrl(WMS_MITECO,      'NZ.Flood.FluvialT500')
    if (key === 'idecan_zari')    return legendUrl(WMS_IDECAN_ZARI, 'ZARI',                      'version=1.3.0')
    if (key === 'ign_erupciones') return legendUrl(WMS_IGN_VULCAN,  'erupciones',                'version=1.3.0')
    if (key === 'igme_bdmoves')   return legendUrl(WMS_IGME_MOVES,  '0',                         'version=1.3.0')
    if (key === 'icgc_riscs')     return legendUrl(WMS_ICGC_RISC,   'G6FIA_PN',                  'version=1.3.0')
    return null
  }

  return (
    <div>
      <div style={{ position: 'relative', height, border: '1px solid #DDD5C8', borderRadius: 4, overflow: 'hidden' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </div>

      <div style={{ marginTop: 10, padding: '10px 12px', background: '#FDFAF6', border: '1px solid #DDD5C8', borderRadius: 4 }}>
        <div style={{ fontSize: '0.72rem', color: '#7A6A5A', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Leyenda del mapa
        </div>

        {activeLayers.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
            marginBottom: 10,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            {activeLayers.map(k => {
              const url = legendUrlFor(k)
              const remote = legendRemoteFor(k)
              if (!url) return null
              const doc = LAYER_DOCS.find(d => d.key === k)
              const isSplit = doc?.mode === 'split'
              const isClip  = doc?.mode === 'clip'
              return (
                <div key={k} style={{
                  display: 'grid',
                  gridTemplateRows: '140px auto',
                  gap: 8,
                  padding: 10,
                  background: '#FFF',
                  border: '1px solid #EDE7DE',
                  borderRadius: 4,
                }}>
                  {isSplit ? (
                    <div style={{ height: 140, background: '#FDFAF6', border: '1px solid #EDE7DE', borderRadius: 3, padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <a href={remote || url} target="_blank" rel="noopener noreferrer"
                         title="Abrir leyenda oficial WMS a tamaño completo"
                         style={{ display: 'block', width: '100%' }}>
                        <SplitLegend url={url} splits={doc.splits || 3} height={128} />
                      </a>
                    </div>
                  ) : isClip ? (
                    <a href={remote || url} target="_blank" rel="noopener noreferrer"
                       title="Abrir leyenda oficial WMS a tamaño completo"
                       style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FDFAF6', border: '1px solid #EDE7DE', borderRadius: 3, padding: 8, height: 140 }}>
                      {/* Recorte del primer degradado (barra azul y=78-283 de la imagen MITECO 131×1995) */}
                      <div style={{
                        width: 22,
                        height: '100%',
                        border: '1px solid #DDD5C8',
                        background: `#FFF url("${url}") no-repeat`,
                        backgroundSize: '655% 968%',
                        backgroundPosition: '0% 4.36%',
                      }} />
                    </a>
                  ) : (
                    <a href={remote || url} target="_blank" rel="noopener noreferrer"
                       title="Abrir leyenda oficial WMS a tamaño completo"
                       style={{
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center',
                         background: '#FDFAF6',
                         border: '1px solid #EDE7DE',
                         borderRadius: 3,
                         height: 140,
                         overflow: 'hidden',
                       }}>
                      <img src={url} alt={`Leyenda ${k}`} loading="lazy"
                           style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />
                    </a>
                  )}
                  <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#4A4A4A', lineHeight: 1.35 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#2C2017' }}>{doc?.label || k}</div>
                    <div style={{ fontSize: 11, color: '#7A6A5A', marginTop: 2 }}>{doc?.note || ''}</div>
                    <div style={{ fontSize: 10, color: '#B0A89E', marginTop: 4, fontStyle: 'italic' }}>
                      {isSplit ? 'Cacheada localmente · dividida en 3 tiras · click para WMS oficial' : 'Cacheada localmente · click para WMS oficial'}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: 8,
          fontFamily: "'DM Sans', sans-serif",
        }}>
          {LAYER_DOCS
            .filter(row => {
              if (row.key.startsWith('pcivil_') || row.key === 'icgc_riscs') return isCatalunya
              if (row.key === 'idecan_zari' || row.key === 'ign_erupciones') return isCanarias
              return true  // nacionales (ncse02, pga475, bdmoves, miteco_t500)
            })
            .map(row => (
            <div key={row.key} style={{ padding: '8px 10px', background: '#FFF', border: '1px solid #EEE7DA', borderRadius: 3 }}>
              <div style={{ fontWeight: 600, color: '#2C2017', fontSize: 12 }}>{row.label}</div>
              <div style={{ color: '#7A6A5A', fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>{row.note}</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: '0.65rem', color: '#B0A89E', marginTop: 8, fontStyle: 'italic', lineHeight: 1.55 }}>
          {isCatalunya && (
            <>
              Fuentes oficiales: IGN geofísica (NCSE-02 · PGA 475), Generalitat Protecció Civil (perill / risc greu incendis), ICGC (riscos geològics 1:25 000), IGME BDMoves (movimientos del terreno nacional), MITECO SNCZI (T=500 contexto). Directiva 2007/60/CE · NCSE-02 (RD 997/2002) · Decret 64/1995.
              <br/>
              <strong>Meteoalerta AEMET (viento, lluvia, nieve, calor)</strong>: <a href="https://www.aemet.es/es/eltiempo/prediccion/avisos?w=hoy&a=cat" target="_blank" rel="noopener noreferrer" style={{color: '#8D9A2E'}}>aemet.es · Meteoalerta Cataluña</a>.
            </>
          )}
          {isCanarias && (
            <>
              Fuentes oficiales: IGN geofísica (NCSE-02 · PGA 475), IDECanarias/Gobierno de Canarias (ZARI · Decreto 100/2002), IGN Vulcanología (erupciones históricas · Red de Vigilancia Volcánica), IGME BDMoves (movimientos del terreno nacional), MITECO SNCZI (T=500 contexto). NCSE-02 (RD 997/2002) · Directiva 2007/60/CE · PEVOLCA (RD 407/1992).
              <br/>
              <strong>Meteoalerta AEMET (viento, calima, lluvia)</strong>: <a href="https://www.aemet.es/es/eltiempo/prediccion/avisos?w=hoy&a=can" target="_blank" rel="noopener noreferrer" style={{color: '#8D9A2E'}}>aemet.es · Meteoalerta Canarias</a>.
            </>
          )}
          {!isCatalunya && !isCanarias && `Fuentes oficiales: IGN geofísica (NCSE-02 · PGA 475), IGME BDMoves (movimientos del terreno), MITECO SNCZI (T=500 contexto). El detalle de incendios forestales se consulta contra la autoridad autonómica competente (CCAA detectada: ${projectCCAA || 'sin determinar — asegúrate de rellenar Provincia en Datos Proyecto'}). NCSE-02 (RD 997/2002) · Directiva 2007/60/CE.`}
        </div>
      </div>
    </div>
  )
}
